import { Injectable } from '@angular/core';
import { CacheStorageRecord } from '../../models/cache-storage-record';
import { Observable, of } from 'rxjs';
// import { isEmpty, isString, isNumber, isDate } from 'lodash';
// Commented below line to import seperate lodash modules to reduce bundle size
/* import {isEmpty} from 'lodash/isEmpty';
import {isString} from 'lodash/isString';
import {isNumber} from 'lodash/isNumber';
import {isDate} from 'lodash/isDate'; */

import { CacheStorageService } from './cache-storage.service';
import { LoggingService } from './../logging/logging.service';
import { LoggingConstants } from './../../constants/logging-constant';
import { DomStorageFallbackService } from './../DomStorageFallback.service';
import { flatMap, map, mergeMap } from 'rxjs/operators';
import { AppUtility } from './../../utils/utility';

@Injectable()
export class LocalCacheService {

    /* istanbul ignore next */
    public static defaultExpires: number = 900; // By default : 30 min

    /* istanbul ignore next */
    constructor(private cacheStorageService: CacheStorageService, private loggingService: LoggingService, private domStorageFB: DomStorageFallbackService, private appUtility: AppUtility) {
        try {
            let lrJSONData = JSON.parse(this.domStorageFB.getItem('lr_json_data'));
            if (lrJSONData && lrJSONData.hasOwnProperty('sessionExpiryTime')) {
                LocalCacheService.defaultExpires = JSON.parse(this.domStorageFB.getItem('lr_json_data')).sessionExpiryTime * 60;
            } else {
                LocalCacheService.defaultExpires = 900;
            }
        } catch (error) {
            LocalCacheService.defaultExpires = 900;
            this.loggingService._debug('Error parsing session expiry time from LR.', 'Local cache service', LoggingConstants.ERROR, error);
            if (LoggingService.uConsole) {
                console.log('In LocalCacheService()', error);
            }
        }
    }

    /**
       * Cache or use result from observable
       *
       * If cache key does not exist or is expired, observable supplied in argument is returned and result cached
       *
       * @param key
       * @param observable
       * @param expires : TimeSpan after which the cache will expire
       * It wil fetch from db & then check current time.
       * @returns {Observable<T>}
    **/

    /* istanbul ignore next */
    public customObservable<T>(key: string, observable: Observable<T>, instanceName: string, expires: number = LocalCacheService.defaultExpires): Observable<T> {
        // 1st : try to fetch item from Local storage, even if it may not be there.
        return this.cacheStorageService.getItem(key, instanceName).pipe(
            map((val: CacheStorageRecord) => {
                // Check for cash expiry: nullify it if yes, otherwise Let it pass
                if (val) {
                    if ((new Date(val.expires)).getTime() > Date.now() || this.domStorageFB.isValidToken(this.domStorageFB.getTokenValue('alightPersonSessionToken', '', true))) {
                        if (LoggingService.uConsole) {
                            console.log('Inside Map If');
                        }

                        // UFD-1558, UFD-2146
                        if (this.appUtility.isSwFeatureEnabled() && this.appUtility.isPostAuth() && (new Date(val.expires)).getTime() < Date.now()) {
                            return null;
                        }
                        return val;
                    } else {
                        if (LoggingService.uConsole) {
                            console.log('Inside Map Else');
                        }
                        return null;
                    }
                }
                return null;
            }),
            // this point will come when the requested value is not cached or it has expired its expiry time.
            // so we just cache the value & Simply return the observable.
            flatMap((val: CacheStorageRecord | null) => {
                if (val && val.value) {
                    if (LoggingService.uConsole) {
                        console.log('flatmap If');
                    }
                    return of(val.value);
                } else {
                    if (LoggingService.uConsole) {
                        console.log('flatmap Else');
                    }
                    return observable.pipe(flatMap((value: any) => this.value(key, value, expires, instanceName))); // The result may have 'expires' explicitly set
                }
            }),
        );
    }
    /**
     * Cache supplied value until expiry
     *
     * @param key
     * @param value
     * @param expires
     * @returns {Observable<T>}
     */

    /* istanbul ignore next */
    private value<T>(key: string, value: T, expires: number | string | Date = LocalCacheService.defaultExpires, instanceName: string): Observable<T> {
        let _expires: Date = this.sanitizeAndGenerateDateExpiry(expires);
        let userSwDataKey: string = '';
        if (LoggingService.uConsole) {
            console.log('In LocalCacheService.value()', key, value);
        }
        let aRHeader = this.domStorageFB.getItem('alightRequestHeader');
        let userSwKey = this.domStorageFB.getItem('swUdIndexKey');
        let orgName = this.appUtility.getOrgName().toLowerCase(); // UFD-3872
        if (aRHeader && this.appUtility.isSwFeatureEnabled()) {
            aRHeader = JSON.parse(aRHeader);
            if (aRHeader['roleId']) {
                let locale = this.appUtility.getLocaleValue();
                if (locale) {
                    if (userSwKey) {
                        userSwDataKey = orgName + '__' + aRHeader['roleId'] + '__' + locale + '__' + userSwKey;
                    } else {
                        userSwDataKey = orgName + '__' + aRHeader['roleId'] + '__' + locale;
                    }
                } else {
                    userSwDataKey = orgName + '__' + aRHeader['roleId'];
                }

                let buildNumber = null;
                let swBuildNo = this.domStorageFB.getItem('swAfBuildNo');
                if (swBuildNo) {
                    buildNumber = swBuildNo;
                } else {
                    if (this.appUtility.isAfEnabled() && this.appUtility.isLiferay() === false) {
                        let app_url = this.domStorageFB.getItem('app_url');
                        if (app_url) {
                            let buildNumberIndex = app_url.substring(0, app_url.length - 1).lastIndexOf('/');
                            buildNumber = app_url.substring(buildNumberIndex + 1, app_url.length - 1);
                            this.domStorageFB.setItem('swAfBuildNo', buildNumber);
                        }
                    }
                }
                if (buildNumber) {
                    userSwDataKey = userSwDataKey + '__' + buildNumber;
                }
            }
        }
        this.loggingService._debug('In LocalCacheService.value()' + key + value, 'UI Core', LoggingConstants.INFO);
        if (this.appUtility.isSwFeatureEnabled()) {
            value = this.appUtility.storePIDataIntoSessionStorage(key, value, instanceName);
            if (LoggingService.uConsole) {
                console.log('localcacheService with SW flag on ', key, value);
            }
            return this.cacheStorageService.setItem(key, {
                expires: _expires,
                swdataid: userSwDataKey,
                value: value
            }, instanceName).pipe(flatMap(val => of(value))).pipe(map(val => value));
        } else {
            if (LoggingService.uConsole) {
                console.log('localcacheService with SW flag off ', key, value);
            }
            return this.cacheStorageService.setItem(key, {
                expires: _expires,
                value: value
            }, instanceName).pipe(flatMap(val => of(value))).pipe(map(val => value));
        }
    }
    /**
     *
     * @param expires
     * @returns {Date}
     */

    /* istanbul ignore next */
    private sanitizeAndGenerateDateExpiry(expires: string | number | Date): Date {
        let expiryDate: Date = this.expiryToDate(expires);

        // Dont allow expiry dates in the past
        if (expiryDate.getTime() <= Date.now()) {
            return new Date(Date.now() + LocalCacheService.defaultExpires);
        }

        return expiryDate;
    }

    /**
     *
     * @param expires
     * @returns {Date}
     */

    /* istanbul ignore next */
    private expiryToDate(expires: any): Date {
        if (!isNaN(expires)) {
            return new Date(Date.now() + Math.abs(expires) * 1000);
        } else if (expires instanceof Date) {
            return expires;
        } else if (expires) {
            return new Date(expires);
        } else {
            return new Date();
        }
    }

    /**
     * removeItemFromIDBkey
     * Function for removing key from Indexed dB storage.
     */

    /* istanbul ignore next */
    public removeItemFromIDBStorage(key: string, storeName: string) {
        return this.cacheStorageService.removeItem(key, storeName);
    }

    /**
    * Clearing all IndexedDB stores stores purpose
    */

    /* istanbul ignore next */
    public clear_ALLIDBStores() {
        this.cacheStorageService.clear_ALLIDBStores();
    }

    /* istanbul ignore next */
    public clear_ALLIDBStores_SW() {
        this.cacheStorageService.clear_ALLIDBStores_SW();
    }

}
